import type { Events } from './updateRowInQueries'
import type { WSPayload } from '@features/websocket/socketService'

export type ID = number | string
type SocketRow = Record<string, unknown>
type MapPayload = (payload: WSPayload) => SocketRow
type UpdateRowFn = (data: SocketRow, payloadEvent: Events) => void

const isRecord = (value: unknown): value is SocketRow => typeof value === 'object' && value !== null

/**
 * Extracts an ID from a record using the given key.
 */
export const getIdFromRecord = (value: unknown, idKey: string): ID | undefined => {
  if (!isRecord(value)) return undefined

  const id = value[idKey]

  return typeof id === 'string' || typeof id === 'number' ? id : undefined
}

/**
 * Handles `retrieve_resources` response where BE sends `{ [resourceKey]: [...rows] }` without an `event` field.
 * Returns true if it handled the payload.
 */
export const handleResourceKeyResponse = (
  payload: WSPayload,
  resourceKey: string | undefined,
  mapPayload: MapPayload | undefined,
  updateRow: UpdateRowFn,
): boolean => {
  if (!resourceKey || payload?.event) return false

  const rows = payload?.[resourceKey as keyof WSPayload]
  if (!Array.isArray(rows)) return false
  ;(rows as SocketRow[]).forEach((row) => {
    const mapped = mapPayload ? mapPayload(row as WSPayload) : row
    updateRow(mapped, 'message')
  })

  return true
}

/**
 * Handles batch mode where the entire collection is replaced.
 * Returns the rows array if handled, undefined otherwise.
 */
export const handleBatchResponse = (
  payload: WSPayload,
  mapPayload: MapPayload | undefined,
): SocketRow[] | undefined => {
  const data = mapPayload ? mapPayload(payload) : payload?.data

  return data ? (data as unknown as SocketRow[]) : undefined
}

// Keys that are routing/meta identifiers, not domain data
const META_KEYS = new Set(['id', 'resource_id', 'resource_type', 'ids_to_be_refetched'])

export type PayloadClassification = {
  isFullData: boolean
  idsToRefetch: ID[] | null
}

/**
 * Determines whether a payload has full domain data or is a lightweight notification.
 * Also extracts `ids_to_be_refetched` if present.
 */
export const classifyPayload = (data: unknown, idKey: string, event: Events | undefined): PayloadClassification => {
  const isSnapshotEvent = event === 'message'

  const metaKeys = idKey === 'id' ? META_KEYS : new Set([...META_KEYS, idKey])
  const hasDomainFields = isRecord(data) && Object.keys(data).some((key) => !metaKeys.has(key))
  const isFullData = isSnapshotEvent || hasDomainFields

  const idsToRefetch =
    isRecord(data) && Array.isArray(data.ids_to_be_refetched) ? (data.ids_to_be_refetched as ID[]) : null

  return { isFullData, idsToRefetch }
}

/**
 * Splits IDs into visible and non-visible sets.
 * Created events are always treated as visible (new rows should appear in the table).
 */
export const partitionByVisibility = (
  ids: ID[],
  isVisibleRow: (id: ID) => boolean,
  isCreatedEvent: boolean,
): { visibleIds: ID[]; nonVisibleIds: ID[] } => {
  const visibleIds: ID[] = []
  const nonVisibleIds: ID[] = []

  for (const id of ids) {
    if (isVisibleRow(id) || isCreatedEvent) {
      visibleIds.push(id)
    } else {
      nonVisibleIds.push(id)
    }
  }

  return { visibleIds, nonVisibleIds }
}
